import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AddOpNoteRequest } from '@api/models/ClientSupportApi/Contract/Notes/add-op-note-request';
import {
    AbstractNoteFormField,
    AbstractNoteFormService,
} from '@ui/base/form-abstract-note/abstract-note.form.service';
import { requiredWithTrimValueValidator } from '@libs/validator/required-with-trim-value.validator';
import { FormTextareaComponent } from '@ui/form/form-textarea/form-textarea.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from '@ui/form/button/button.module';
import { DialogActions } from '@ui/dialog-v2/dialog-actions/dialog-actions';


export interface AbstractNoteFormBody {
    note?: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-form-abstract-note',
    templateUrl: './form-abstract-note.component.html',
    styleUrls: ['./form-abstract-note.component.scss'],
    providers: [AbstractNoteFormService],
    imports: [
        FormTextareaComponent,
        ReactiveFormsModule,
        TranslatePipe,
        ButtonModule,
        DialogActions,
    ],
})
export class FormAbstractNoteComponent {
    /**
     * Reset note field after submit form flag
     */
    @Input() public resetFieldAfterSubmit: boolean = true;
    /**
     * Server pending flag
     */
    @Input() public pending: boolean;
    /**
     * Submit form event
     *
     * @returns form data
     */
    @Output() public submitForm: EventEmitter<AddOpNoteRequest> =
        new EventEmitter<AddOpNoteRequest>();

    readonly #abstractNoteFormService: AbstractNoteFormService = inject(AbstractNoteFormService);

    /**
     * Form object
     */
    public readonly form: FormGroup = this.#abstractNoteFormService.createReactiveForm();

    protected readonly abstractNoteFormField: typeof AbstractNoteFormField = AbstractNoteFormField;

    protected get noteControl(): AbstractControl {
        return this.form.get(AbstractNoteFormField.Note);
    }

    /**
     * Required note flag
     */
    @Input() public set noteRequired(flag: boolean) {
        if (flag) {
            this.noteControl.setValidators([requiredWithTrimValueValidator()]);
        } else {
            this.noteControl.clearValidators();
        }
    }

    public handlerSubmit(): void {
        this.submitForm.emit(this.form.getRawValue() as AbstractNoteFormBody);

        if (this.resetFieldAfterSubmit) {
            this.noteControl.clearValidators();
            this.noteControl.reset();
        }
    }
}
